import React, { useState, useRef, useEffect, useCallback } from 'react';
import { QuantityPicker } from 'react-qty-picker';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

import { commerceProductChangeQuantity } from '@uncinc/react-drupal-core/dist/redux/actions/Commerce';

import CartProductImage from './CartProductImage';
import DiscountTierAccordion from '../../../../components/DiscountTierAccordion';
import { getDiscountedPrice } from '../../../../components/DiscountTierAccordion/helpers';
import { formatPrice } from '../../../../helpers/commerce';
import './style.scss';
import RemoveProductModal from '../RemoveProductModal';

/**
 * Debounce delay for quantity API calls (ms).
 * Batches rapid clicks into a single request to prevent
 * OrderVersionMismatchException on the backend.
 */
const QUANTITY_UPDATE_DEBOUNCE_MS = 400;

const CartProduct = ({
  title,
  type,
  price,
  priceNumber = 0,
  quantity,
  maximumQuantity,
  discountTiers = [],
  id,
  image,
  body = null,
  date = null,
  location = null,
  readOnly,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const isTraining = !!type.includes('fysieke-training');
  const minimumQuantity = 1;

  // Volume discounts are always a percentage, so derive the discounted unit
  // price from the active tier rather than relying on extra cart data. The
  // read-only (confirmation) view shows the line total, so skip the discount.
  const discountedPrice = readOnly
    ? null
    : getDiscountedPrice(priceNumber, discountTiers, quantity);

  // The price sits top-right in the header on desktop but moves to the bottom
  // row on mobile, so it is rendered in both places and toggled per breakpoint.
  const renderPrice = (placement) => (
    <div
      className={`CartProduct__details__price CartProduct__details__price--${placement}`}
    >
      {discountedPrice !== null ? (
        <>
          <strong className="CartProduct__details__price-current">
            {t('cart.price_display', {
              price: formatPrice(discountedPrice),
              interpolation: { escapeValue: true },
            })}
          </strong>
          <span className="CartProduct__details__price-original">{price}</span>
        </>
      ) : (
        <strong>{price}</strong>
      )}
    </div>
  );

  // Ref for debounce timer — cleared on each new input so only the
  // final value in a burst of clicks is sent to the backend
  const debounceTimerRef = useRef(null);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Debounced quantity change handler.
   * Cancels any pending update and schedules a new one so that only
   * the final value after rapid clicks is sent to the backend.
   */
  const handleQuantityChange = useCallback(
    (value) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        dispatch(commerceProductChangeQuantity(id, value));
      }, QUANTITY_UPDATE_DEBOUNCE_MS);
    },
    [dispatch, id],
  );

  return (
    <div className="CartProduct">
      <div className="CartProduct__image">
        <CartProductImage image={image} />
      </div>
      <div className="CartProduct__details">
        <div className="CartProduct__details__row">
          <div className="CartProduct__details__heading">
            <div className="CartProduct__details__title">{title}</div>
            {body && <div className="CartProduct__details__body">{body}</div>}
            {location && (
              <div className="CartProduct__details__location">{location}</div>
            )}
            {date && (
              <div className="CartProduct__details__date">
                {format(new Date(date), 'dd-MM-YY HH:mm')}
              </div>
            )}
          </div>
          {readOnly && (
            <div>
              <strong>{quantity}x</strong>
            </div>
          )}
          {readOnly ? (
            <div className="CartProduct__details__price readOnly">
              <strong>{price}</strong>
            </div>
          ) : (
            renderPrice('desktop')
          )}

          {!readOnly && (
            <div
              onClick={() => setShowModal(true)}
              className="icon icon-cross"
            />
          )}
        </div>

        {discountTiers.length > 0 && (
          <div className="CartProduct__details__tiers">
            <DiscountTierAccordion tiers={discountTiers} activeQty={quantity} />
          </div>
        )}
        <div className="CartProduct__details__row CartProduct__details__row--actions">
          {!readOnly && !isTraining && (
            <div className="CartProduct__details__quantity">
              <QuantityPicker
                min={minimumQuantity}
                max={maximumQuantity || 500}
                value={quantity}
                onChange={handleQuantityChange}
                width="2.9rem"
              />
            </div>
          )}
          {!readOnly && renderPrice('mobile')}
        </div>
      </div>
      {showModal && (
        <RemoveProductModal
          showModal={showModal}
          id={id}
          setShowModal={setShowModal}
        />
      )}
    </div>
  );
};

export default CartProduct;
